import { VStack } from "@chakra-ui/react"
import { useState, useMemo, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { getAlertSeverityColor } from "@hydrologiq/shared/components/AlertSeverityBadge"
import Figure from "@hydrologiq/shared/figures/generic/Figure"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import { KanbanBoard, KANBAN_BOARD_GAPS } from "@hydrologiq/shared/components/kanban"
import type { KanbanViewMode } from "@hydrologiq/shared/components/kanban"
import { useAlertsKanban } from "../hooks/useAlertsKanban"
import { useUpdateAlertStatus } from "@hydrologiq/shared/hooks/useAlertMutations"
import { useUpdateAlert } from "@hydrologiq/shared/hooks/useAlertUpdate"

function AlertsKanban() {
  const [searchParams, setSearchParams] = useSearchParams()
  const highlightParam = searchParams.get("highlight")

  const [kanbanViewMode, setKanbanViewMode] = useState<KanbanViewMode>("ungrouped")

  const { data, loading, error, refetch } = useAlertsKanban()
  const { updateStatus, loading: statusLoading } = useUpdateAlertStatus()
  const { update: updateAlert, loading: updateLoading } = useUpdateAlert()

  const isAssetId = useMemo(() => highlightParam && !highlightParam.startsWith("alert-"), [highlightParam])
  const highlightedAlertId = useMemo(() => (isAssetId ? undefined : highlightParam || undefined), [isAssetId, highlightParam])
  const highlightedAssetId = useMemo(() => (isAssetId ? highlightParam || undefined : undefined), [isAssetId, highlightParam])

  const alerts = useMemo(() => data?.alerts ?? {
    new: [],
    in_progress: [],
    on_hold: [],
    completed: [],
  }, [data?.alerts])

  const assignableUsers = useMemo(() => data?.assignable_users ?? [], [data?.assignable_users])
  const systemOrderIds = useMemo(() => data?.system_order_ids ?? [], [data?.system_order_ids])
  const pinnedSystemIds = useMemo(() => data?.pinned_system_ids ?? [], [data?.pinned_system_ids])

  const headlines = data?.headlines ?? {}

  const handleStatusChange = useCallback(async (
    alertId: string,
    newStatus: "new" | "acknowledged" | "attended" | "on_hold" | "completed",
    note?: string,
  ) => {
    await updateStatus(alertId, newStatus, note)
    refetch()
  }, [updateStatus, refetch])

  const handleAttended = useCallback(async (alertId: string) => {
    await updateStatus(alertId, "attended", "Attended")
    refetch()
  }, [updateStatus, refetch])

  const handleUpdateAlert = useCallback(async (alertId: string, input: Parameters<typeof updateAlert>[1]) => {
    await updateAlert(alertId, input)
    refetch()
  }, [updateAlert, refetch])

  const handleHighlightDismiss = useCallback(() => {
    setSearchParams({})
  }, [setSearchParams])

  const newIssues = headlines.new_issues
  const inProgressIssues = headlines.in_progress_issues
  const onHoldIssues = headlines.on_hold_issues
  const customersAffected = headlines.customers_affected ?? 0
  const systemsAffected = headlines.systems_affected ?? 0
  const resolutionTime = headlines.resolution_time_hours
  const resolutionTimePrior = headlines.resolution_time_hours_7_14d

  const formatHours = (h: number) =>
    `${Math.floor(h)}h ${Math.floor((h % 1) * 60)}m`
  const resolutionTimeStr =
    resolutionTime != null ? formatHours(resolutionTime) : "—"
  const resolutionTimePriorStr =
    resolutionTimePrior != null ? formatHours(resolutionTimePrior) : null

  const criticalColor = getAlertSeverityColor("critical")
  const warningColor = getAlertSeverityColor("warning")
  const okColor = "green"
  const labelFontSize = {
    base: "sm",
    lg: "md",
    "2xl": "md",
    "4xl": "lg",
  } as const
  const valueFontSize = {
    base: "md",
    lg: "lg",
    "2xl": "xl",
    "4xl": "2xl",
  } as const
  const warningFontWeight = "semibold"

  if (error) {
    return (
      <VStack align="stretch" gap={4} w="100%">
        <Figure heading="Error">
          <FigureContent.Root>
            <FigureContent.Value color="red">
              Failed to load alerts
            </FigureContent.Value>
          </FigureContent.Root>
        </Figure>
      </VStack>
    )
  }

  return (
    <VStack align="stretch" gap={KANBAN_BOARD_GAPS} w="100%">
      <FigureGrid loading={loading}>
        <Figure heading="New Issues">
          <FigureContent.Root>
            <FigureContent.Value
              color={newIssues?.critical ? criticalColor : okColor}
              fontSize={valueFontSize}
            >
              {newIssues?.critical ?? 0} Critical
            </FigureContent.Value>
            {newIssues && (
              <FigureContent.Label
                color={newIssues?.warning ? warningColor : okColor}
                fontSize={labelFontSize}
                fontWeight={warningFontWeight}
              >
                {newIssues.warning ?? 0} Warning
              </FigureContent.Label>
            )}
          </FigureContent.Root>
        </Figure>
        <Figure heading="In Progress">
          <FigureContent.Root>
            <FigureContent.Value
              color={
                inProgressIssues?.critical ? criticalColor : okColor
              }
              fontSize={valueFontSize}
            >
              {inProgressIssues?.critical ?? 0} Critical
            </FigureContent.Value>
            {inProgressIssues && (
              <FigureContent.Label
                color={
                  inProgressIssues?.warning ? warningColor : okColor
                }
                fontSize={labelFontSize}
                fontWeight={warningFontWeight}
              >
                {inProgressIssues.warning ?? 0} Warning
              </FigureContent.Label>
            )}
          </FigureContent.Root>
        </Figure>
        <Figure heading="On Hold">
          <FigureContent.Root>
            <FigureContent.Value
              color={
                onHoldIssues?.critical ? criticalColor : okColor
              }
              fontSize={valueFontSize}
            >
              {onHoldIssues?.critical ?? 0} Critical
            </FigureContent.Value>
            {onHoldIssues && (
              <FigureContent.Label
                color={
                  onHoldIssues?.warning ? warningColor : okColor
                }
                fontSize={labelFontSize}
                fontWeight={warningFontWeight}
              >
                {onHoldIssues.warning ?? 0} Warning
              </FigureContent.Label>
            )}
          </FigureContent.Root>
        </Figure>
        <Figure heading="Customers Affected">
          <FigureContent.Root>
            <FigureContent.Value fontSize={valueFontSize}>
              {customersAffected} customer{customersAffected !== 1 ? "s" : ""}
            </FigureContent.Value>
            <FigureContent.Label
              fontSize={labelFontSize}
              fontWeight={warningFontWeight}
            >
              {systemsAffected} system{systemsAffected !== 1 ? "s" : ""}
            </FigureContent.Label>
          </FigureContent.Root>
        </Figure>
        <Figure heading="Resolution Time">
          <FigureContent.Root>
            <FigureContent.Value fontSize={valueFontSize}>
              {resolutionTimeStr}
            </FigureContent.Value>
            {resolutionTimePriorStr && (
              <FigureContent.Label
                fontSize={labelFontSize}
                fontWeight={warningFontWeight}
              >
                vs {resolutionTimePriorStr} (7–14d ago)
              </FigureContent.Label>
            )}
          </FigureContent.Root>
        </Figure>
      </FigureGrid>

      {!loading && (
        <KanbanBoard
          alerts={alerts}
          onStatusChange={handleStatusChange}
          onAttended={handleAttended}
          onUpdate={handleUpdateAlert}
          isUpdating={statusLoading || updateLoading}
          enableSearch={true}
          isPreview={false}
          assignableUsers={assignableUsers}
          viewMode={kanbanViewMode}
          onViewModeChange={setKanbanViewMode}
          systemOrderIds={systemOrderIds}
          pinnedSystemIds={pinnedSystemIds}
          highlightedAlertId={highlightedAlertId}
          highlightedAssetId={highlightedAssetId}
          onHighlightDismiss={handleHighlightDismiss}
        />
      )}
    </VStack>
  )
}

export default AlertsKanban
